import React, { useState, useMemo, Fragment } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton,
} from '@headlessui/react';
import { Search, X, ChevronDown, Check, Info, Folder } from 'lucide-react';
import { useGetAllTasksQuery } from 'service/project/hooks/useGetAllTasksQuery';
import { useDebounce } from 'hooks/useDebounce';
import { VALID_PARENT_CATEGORIES } from 'utils/helpers';
import { WorkTypeCategory, WorkType } from 'types/project/projectDefaults';
import { workTypeUIMap } from '../modules/ProjectManagement/Dropdowns/TaskTypeDropdown';
import { Task } from 'types/project/tasks';
import { LoaderButton } from 'components/common/LoadingButton';

interface ChangeParentModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  workItemUuid: string;
  currentWorkItemKey?: string;
  currentWorkTypeCategory: WorkTypeCategory;
  currentParentUuid: string | null;
  currentParentTitle?: string;
  availableWorkTypes: WorkType[];
  /** `newParent` is the picked item as listed, e.g. to see whether it is a Done Epic; null for no parent. */
  onSave: (newParentUuid: string | null, newParent: Task | null) => Promise<boolean>;
  isSaving?: boolean;
}

const ChangeParentModal: React.FC<ChangeParentModalProps> = ({
  isOpen,
  onClose,
  projectId,
  workItemUuid,
  currentWorkItemKey,
  currentWorkTypeCategory,
  currentParentUuid,
  currentParentTitle,
  availableWorkTypes,
  onSave,
  isSaving = false,
}) => {
  const formatCategory = (category: string) => {
    if (!category) return '';
    const cat = category.toUpperCase();
    if (cat === 'SUB_TASK') return 'Sub-task';
    if (cat === 'USER_STORY') return 'User Story';
    return cat.charAt(0) + cat.slice(1).toLowerCase();
  };

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const comboboxBtnRef = React.useRef<HTMLButtonElement>(null);

  // Derive allowed parent categories based on HIERARCHY_RULES inverse mapping
  const allowedParentCategories = VALID_PARENT_CATEGORIES[currentWorkTypeCategory] || [];

  // Map categories to available work type UUIDs
  const allowedParentTypeUuids = useMemo(() => {
    const uuids = availableWorkTypes
      .filter((wt) => allowedParentCategories.includes(wt.workTypeCategory))
      .map((wt) => wt.workTypeUuid);
    return uuids.length > 0 ? uuids : ['NONE'];
  }, [availableWorkTypes, allowedParentCategories]);

  // Fetch candidate parent tasks matching the allowed types
  const { data: taskResponse, isLoading } = useGetAllTasksQuery({
    projectUuid: projectId,
    search: debouncedSearch || undefined,
    workTypeUuidList: allowedParentTypeUuids,
    cursor: null,
    size: 50,
    viewType: 'LIST',
  });

  const rawTasks = taskResponse?.response?.[0]?.data || [];

  // Filter out the work item itself AND the current parent to prevent circular reference / redundant selection
  const candidateTasks = useMemo(() => {
    return rawTasks.filter(
      (task) =>
        task.workItemUuid !== workItemUuid &&
        task.workItemUuid !== currentParentUuid
    );
  }, [rawTasks, workItemUuid, currentParentUuid]);

  // Prepend "No Parent" option only if:
  //   1. The item currently has a parent, AND
  //   2. The work item type is NOT SUB_TASK (SUB_TASKs must always have a parent per hierarchy rules)
  const canBeRootLevel = currentWorkTypeCategory !== WorkTypeCategory.SUB_TASK;
  const candidateListWithNone = useMemo(() => {
    if (!currentParentUuid || !canBeRootLevel) return candidateTasks;
    const noParentItem: Task = {
      workItemUuid: 'REMOVE_PARENT' as any,
      workItemKey: 'REMOVE',
      title: 'No Parent (Root Work Item)',
      workItemTitle: 'No Parent (Root Work Item)',
    } as any;
    return [noParentItem, ...candidateTasks];
  }, [candidateTasks, currentParentUuid, canBeRootLevel]);

  const handleConfirmSave = async () => {
    if (!selectedTask) return;
    const newParentUuid =
      selectedTask.workItemUuid !== 'REMOVE_PARENT'
        ? selectedTask.workItemUuid
        : null;
    const success = await onSave(newParentUuid, newParentUuid ? selectedTask : null);
    if (success) {
      onClose();
    }
  };

  const handleClearSelection = () => {
    setSelectedTask(null);
    setSearchTerm('');
  };

  // Reset local state when opening/closing
  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSelectedTask(null);
    }
  }, [isOpen]);

  const displayTitle = selectedTask
    ? selectedTask.workItemUuid === 'REMOVE_PARENT'
      ? 'No Parent (Root Work Item)'
      : `${selectedTask.workItemKey}: ${selectedTask.title || selectedTask.workItemTitle}`
    : searchTerm;

  const formattedCurrentParent = useMemo(() => {
    if (!currentParentTitle) return 'None';
    return currentParentTitle.includes(':')
      ? currentParentTitle.replace(':', ' \u2013')
      : currentParentTitle;
  }, [currentParentTitle]);

  if (!isOpen) return null;

  return createPortal(
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-[9999]">
        {/* Backdrop overlay */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        {/* Content container */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100 translate-y-0"
            leaveTo="opacity-0 scale-95 translate-y-4"
          >
            <DialogPanel className="w-full max-w-lg transform rounded-2xl bg-[#1c2030] border border-[#2d3142]/40 shadow-2xl transition-all text-left overflow-visible">
              {/* Header */}
              <div className="px-6 pt-6 border-b border-[#2d3142]/40">
                <h3 className="text-xl font-bold text-white tracking-wide">
                  {currentParentUuid ? 'Change Parent Work Item' : 'Add Parent Work Item'}
                </h3>
                <p className="mt-2 text-sm text-[#8e94a5] leading-relaxed">
                  {currentParentUuid
                    ? 'Select a new parent work item. A work item can only belong to one parent at a time.'
                    : 'Select a parent work item for this item. A work item can only belong to one parent at a time.'}
                </p>
                {currentParentUuid && (
                  <div className="mt-2 mb-2 text-sm text-[#8e94a5]">
                    Current parent:{' '}
                    <span className="text-[#8b5cf6] font-semibold">
                      {formattedCurrentParent}
                    </span>
                  </div>
                )}
              </div>

              {/* Form/Selector Section */}
              <div className="p-6 space-y-4 relative">
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-[#8e94a5] uppercase tracking-wider">
                    Parent
                  </label>

                  <Combobox
                    as="div"
                    value={selectedTask}
                    onChange={(task: Task | null) => {
                      setSelectedTask(task);
                      if (task) {
                        if (task.workItemUuid === 'REMOVE_PARENT') {
                          setSearchTerm('No Parent (Root Work Item)');
                        } else {
                          setSearchTerm(`${task.workItemKey}: ${task.title || task.workItemTitle || ''}`);
                        }
                      }
                    }}
                    className="relative w-full"
                  >
                    {({ open }) => (
                      <div className="relative">
                        {/* ── LOCKED STATE: task already selected ────────────────────── */}
                        {selectedTask ? (
                          <div className="bg-[#121420] border border-[#8b5cf6]/50 rounded-xl px-4 py-3 flex items-center min-h-[50px] gap-3">
                            {selectedTask.workItemUuid === 'REMOVE_PARENT' ? (
                              <>
                                <Folder className="w-4 h-4 text-[#8e94a5] shrink-0" />
                                <span className="text-sm text-white flex-1 truncate">No Parent (Root Work Item)</span>
                              </>
                            ) : (() => {
                              const ui = selectedTask.workTypeCategory
                                ? workTypeUIMap[selectedTask.workTypeCategory as keyof typeof workTypeUIMap]
                                : null;
                              return (
                                <>
                                  {ui && <span className={`${ui.color} shrink-0 text-xs`}>{ui.icon}</span>}
                                  <span className="text-xs font-semibold text-[#8b5cf6] shrink-0">{selectedTask.workItemKey}</span>
                                  <span className="text-sm text-white flex-1 truncate">
                                    {selectedTask.title || selectedTask.workItemTitle}
                                  </span>
                                </>
                              );
                            })()}
                            <button
                              type="button"
                              onClick={handleClearSelection}
                              disabled={isSaving}
                              className="shrink-0 p-1 rounded-md text-[#5c6175] hover:text-white hover:bg-[#2d3142]/60 transition-colors disabled:opacity-40"
                              title="Clear selection"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                        /* ── SEARCH STATE: no task selected yet ────────────────────── */
                        <div className="bg-[#121420] border border-[#2d3142]/60 rounded-xl px-4 py-3 flex items-center min-h-[50px] relative focus-within:border-[#8b5cf6]/60 focus-within:ring-1 focus-within:ring-[#8b5cf6]/60 transition-all cursor-pointer">
                          <ComboboxInput
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                            }}
                            value={searchTerm}
                            placeholder="Search and select a parent work item"
                            className="w-full bg-transparent outline-none text-sm text-white placeholder:text-[#5c6175] disabled:opacity-50 pr-8"
                            disabled={isSaving}
                            onFocus={() => {
                              if (!open) comboboxBtnRef.current?.click();
                            }}
                            onClick={() => {
                              if (!open) comboboxBtnRef.current?.click();
                            }}
                          />
                          <ComboboxButton ref={comboboxBtnRef} className="absolute inset-y-0 right-0 flex items-center pr-4">
                            <ChevronDown className="h-5 w-5 text-[#5c6175] hover:text-white transition-colors" />
                          </ComboboxButton>
                        </div>
                        )}

                        <ComboboxOptions className="absolute top-[56px] left-0 w-full max-h-[220px] overflow-auto rounded-xl bg-[#121420] border border-[#2d3142] shadow-2xl py-1.5 z-[99999] empty:hidden">
                          {isLoading && (
                            <div className="px-4 py-3 text-[#8e94a5] text-sm flex items-center gap-2">
                              <svg className="animate-spin h-4 w-4 text-[#8b5cf6]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Searching candidates...
                            </div>
                          )}
                          {!isLoading && candidateListWithNone.length === 0 && (
                            <div className="px-4 py-3 text-[#8e94a5] text-sm">
                              No matches found
                            </div>
                          )}
                          {candidateListWithNone.map((task) => {
                            if (task.workItemUuid === 'REMOVE_PARENT') {
                              return (
                                <ComboboxOption
                                  key="REMOVE_PARENT"
                                  value={task}
                                  className={({ focus }) =>
                                    `relative cursor-pointer select-none py-2.5 px-4 flex items-center justify-between gap-3 transition-colors ${
                                      focus ? 'bg-[#2d3142]/40 text-white' : 'text-[#8e94a5]'
                                    }`
                                  }
                                >
                                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                    <Folder className="w-4 h-4 text-[#8e94a5] shrink-0" />
                                    <span className="text-sm truncate text-white">No Parent (Root Work Item)</span>
                                  </div>
                                  {selectedTask?.workItemUuid === 'REMOVE_PARENT' && <Check className="w-4 h-4 text-[#8b5cf6]" />}
                                </ComboboxOption>
                              );
                            }

                            const category = task.workTypeCategory;
                            const ui = category ? workTypeUIMap[category as keyof typeof workTypeUIMap] : null;
                            const titleText = task.title || task.workItemTitle || 'No Title';
                            const isCurrentlySelected = selectedTask?.workItemUuid === task.workItemUuid;

                            return (
                              <ComboboxOption
                                key={task.workItemUuid}
                                value={task}
                                className={({ focus }) =>
                                  `relative cursor-pointer select-none py-2.5 px-4 flex items-center justify-between gap-3 transition-colors ${
                                    focus ? 'bg-[#2d3142]/40 text-white' : 'text-[#8e94a5]'
                                  }`
                                }
                              >
                                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                  {ui && (
                                    <span className={`${ui.color} shrink-0 text-xs`}>{ui.icon}</span>
                                  )}
                                  <span className="text-xs font-semibold text-[#8b5cf6] shrink-0">
                                    {task.workItemKey}
                                  </span>
                                  <span className="text-sm truncate text-white">{titleText}</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="text-[10px] bg-[#1c2030] px-2 py-0.5 rounded font-semibold text-[#8e94a5] border border-[#2d3142]/40">
                                    {category ? formatCategory(category) : ''}
                                  </span>
                                  {isCurrentlySelected && <Check className="w-4 h-4 text-[#8b5cf6]" />}
                                </div>
                              </ComboboxOption>
                            );
                          })}
                        </ComboboxOptions>
                      </div>
                    )}
                  </Combobox>
                </div>

                {/* Hierarchy Info Footer */}
                {allowedParentCategories.length > 0 && (
                  <div className="flex gap-3 bg-[#121420]/50 border border-[#2d3142]/40 rounded-xl p-4 text-xs text-[#8e94a5] items-start">
                    <Info className="w-4.5 h-4.5 text-[#8b5cf6] shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-semibold text-white">Hierarchy Rules</p>
                      <p className="leading-relaxed">
                        As a <span className="text-[#8b5cf6] font-semibold">{formatCategory(currentWorkTypeCategory)}</span>, this work item can only be moved under:
                      </p>
                      <ul className="list-disc pl-4 space-y-1 mt-1 text-white font-medium">
                        {allowedParentCategories.map((cat) => (
                          <li key={cat}>
                            {formatCategory(cat)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-[#2d3142]/40 flex items-center  bg-[#181c2b] rounded-b-2xl justify-end">
                {/* <div className="text-xs text-[#5c6175] font-medium max-w-[55%] truncate">
                  {currentWorkItemKey && (
                    <>
                      {currentWorkItemKey} is currently{' '}
                      {parentKey ? (
                        <>
                          assigned to{' '}
                          <span className="text-[#8b5cf6] font-semibold">{parentKey}</span>
                        </>
                      ) : (
                        'not assigned to any parent'
                      )}
                    </>
                  )}
                </div> */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSaving}
                    className="px-5 py-2.5 text-sm font-semibold text-[#8e94a5] hover:text-white transition-all disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <LoaderButton
                    className={`!m-0 !py-2.5 !px-6 rounded-xl font-bold bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] hover:from-[#4f46e5] hover:to-[#7c3aed] text-white transition-all min-h-[42px] hover:shadow-lg hover:shadow-[#8b5cf6]/20 disabled:opacity-50 disabled:cursor-not-allowed`}
                    text="Save"
                    isLoading={isSaving}
                    disabled={!selectedTask || isSaving}
                    onClick={handleConfirmSave}
                  />
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>,
    document.body
  );
};

export default ChangeParentModal;
